// src/api/serverTime.js
//
// クライアントの「いま」をサーバ時刻基準で取得するためのユーティリティ。
// Firebase Firestore には「現在時刻 API」がないので、
//   1) ログイン中ユーザーの users/{uid} ドキュメントに `_clockSync: serverTimestamp()` を1回書き込む
//   2) 書いた直後に読み戻して「サーバ時刻 − クライアント時刻」のオフセットを計算
//   3) 以降は `Date.now() + offset` を「サーバ時刻」として参照
// する仕組み。
//
// - 同期は遅延（最初に必要になったとき）に1回だけ走る
// - 失敗時はローカル時計にフォールバック（ログ警告のみ）

import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase";

let offsetMs = 0;
let synced = false;
let inflight = null;

async function performSync() {
  const current = auth.currentUser;
  if (!current) {
    // 未ログインなら同期できない。ローカル時計のまま。
    return;
  }
  try {
    const ref = doc(db, "users", current.uid);
    const beforeWrite = Date.now();
    await setDoc(ref, { _clockSync: serverTimestamp() }, { merge: true });
    const snap = await getDoc(ref);
    const ts = snap.data()?._clockSync;
    if (ts && typeof ts.toMillis === "function") {
      // 書き込み〜読み戻しのラウンドトリップを考慮し、
      // beforeWrite と Date.now() の中間時点をクライアント側基準として採用する。
      const clientMidpoint = (beforeWrite + Date.now()) / 2;
      offsetMs = ts.toMillis() - clientMidpoint;
      synced = true;
    }
  } catch (err) {
    console.warn("serverTime sync failed; using local clock", err);
  }
}

/**
 * 同期を保証する（未実施なら走らせる）。同時に複数呼ばれても1回で済む。
 */
export function ensureServerTimeSynced() {
  if (synced) return Promise.resolve();
  if (!inflight) {
    inflight = performSync().finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

/**
 * サーバ時刻基準の「現在時刻」をミリ秒で返す。
 * 未同期ならローカル時計と同じ値（フォールバック）。
 */
export function serverNowMs() {
  return Date.now() + offsetMs;
}

/**
 * サーバ時刻に同期済みかどうか（UI 表示用）。
 */
export function isServerTimeSynced() {
  return synced;
}
